import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { AddressFields, FIELD_CLASS, type AddressValue } from '@/components/properties/AddressFields';
import { useAccount, type Account } from '@/api/useAccount';
import {
  CompanySettingsRefusedError,
  LOGO_TYPES,
  useRemoveLogo,
  useSettingsChanges,
  useUpdateAppointmentEmailSettings,
  useUpdateBusinessHours,
  useUpdateCompanyProfile,
  useUpdateInvoiceReminders,
  useUploadLogo,
} from '@/api/useCompanySettings';
import { logoSrc } from '@/lib/logo';
import { CompanyLogo } from '@/components/company/CompanyLogo';
import { formatDate, formatInstantWithZone } from '@/lib/format';
import { DocumentTermsSection } from './DocumentTermsSection';

/**
 * The company's own settings (M2-19): profile, logo, hours and timezone, and
 * the trail of who changed them. Owner and Admin (`company.manage`, gated by
 * the route); every section saves on its own, so a mistake in one never holds
 * up another.
 *
 * Each save answers with the account, which goes straight into the
 * `['account']` cache entry the header, the documents and the board read
 * (`useCompanySettings`).
 */
export function CompanySettings() {
  const { t } = useTranslation();
  const account = useAccount();

  if (account.isPending) {
    return <LoadingState label={t('settings.company.loading')} />;
  }
  if (account.isError) {
    return (
      <ErrorState title={t('settings.company.loadErrorTitle')} onRetry={() => void account.refetch()} />
    );
  }

  return (
    <>
      <ProfileSection account={account.data} />
      <LogoSection account={account.data} />
      <HoursSection account={account.data} />
      <RemindersSection account={account.data} />
      <AppointmentEmailsSection account={account.data} />
      <DocumentTermsSection />
      <RecentChangesSection />
      <section className="space-y-1 rounded-lg border p-4" aria-labelledby="settings-labor-heading">
        <h2 id="settings-labor-heading" className="text-base font-semibold">
          {t('settings.labor.heading')}
        </h2>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('settings.labor.description')}{' '}
          <Link to="/pricebook" className="underline underline-offset-2">
            {t('settings.labor.link')}
          </Link>
        </p>
      </section>
    </>
  );
}

/**
 * A form's values, following what is stored. When the stored values change —
 * this form's own save, or another tab's after a refetch — the form resets to
 * them IN PLACE.
 *
 * ⚠️ Not by re-keying the section, which is what this first did: a remount
 * replaces every element, so the Save button the person had just pressed (and
 * the one the timezone dialog hands focus back to) was gone, and keyboard
 * focus fell to the page's body after every save (found in review).
 */
function useStoredForm<T>(stored: T) {
  const storedKey = JSON.stringify(stored);
  const [values, setValues] = useState(stored);
  const [baseline, setBaseline] = useState(storedKey);
  if (storedKey !== baseline) {
    setBaseline(storedKey);
    setValues(stored);
  }
  return { values, setValues, dirty: JSON.stringify(values) !== storedKey, discard: () => setValues(stored) };
}

function Section({ id, title, description, children }: { id: string; title: string; description?: string; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border p-4" aria-labelledby={id}>
      <div>
        <h2 id={id} className="text-base font-semibold">
          {title}
        </h2>
        {description ? (
          <p className="text-sm text-[var(--color-muted-foreground)]">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

/** A save's outcome, announced: the success politely, a refusal as an alert. */
function Outcome({ saved, error }: { saved: string | null; error: string | null }) {
  return (
    <>
      <p role="status" aria-live="polite" className="text-sm text-[var(--color-muted-foreground)]">
        {saved ?? ''}
      </p>
      {error ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {error}
        </p>
      ) : null}
    </>
  );
}

function errorText(error: unknown, fallback: string) {
  return error instanceof CompanySettingsRefusedError ? error.message : fallback;
}

// --- Profile ---------------------------------------------------------------------

const EMPTY_ADDRESS: AddressValue = { line1: '', line2: '', city: '', state: '', postalCode: '' };

type ProfileValues = { name: string; phone: string; license: string; address: AddressValue };

function storedProfile(account: Account): ProfileValues {
  return {
    name: account.name,
    phone: account.phone ?? '',
    license: account.licenseNumber ?? '',
    address: account.address ? { ...account.address, line2: account.address.line2 ?? '' } : EMPTY_ADDRESS,
  };
}

function ProfileSection({ account }: { account: Account }) {
  const { t } = useTranslation();
  const id = useId();
  const save = useUpdateCompanyProfile();
  const form = useStoredForm(storedProfile(account));
  const { name, phone, license, address } = form.values;
  const [saved, setSaved] = useState<string | null>(null);
  const nameInput = useRef<HTMLInputElement>(null);
  // An address is all or nothing on the API: four fields or none.
  const anyAddress = Object.values(address).some((v) => v.trim() !== '');

  function edit(change: Partial<ProfileValues>) {
    form.setValues((prev) => ({ ...prev, ...change }));
    setSaved(null);
  }

  function discard() {
    form.discard();
    setSaved(null);
    save.reset();
    nameInput.current?.focus();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(null);
    save.mutate(
      {
        name,
        address: anyAddress
          ? { ...address, line2: address.line2.trim() === '' ? null : address.line2 }
          : null,
        phone: phone.trim() === '' ? null : phone,
        licenseNumber: license.trim() === '' ? null : license,
      },
      { onSuccess: () => setSaved(t('settings.company.profileSaved')) },
    );
  }

  return (
    <Section id={`${id}-heading`} title={t('settings.company.profileHeading')} description={t('settings.company.profileDescription')}>
      <form className="space-y-3" onSubmit={submit} aria-labelledby={`${id}-heading`}>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm sm:col-span-2" htmlFor={`${id}-name`}>
            <span className="mb-1 block font-medium">{t('settings.company.name')}</span>
            <input
              id={`${id}-name`}
              ref={nameInput}
              className={FIELD_CLASS}
              value={name}
              required
              maxLength={200}
              autoComplete="organization"
              onChange={(e) => edit({ name: e.target.value })}
            />
          </label>
        </div>
        <AddressFields
          value={address}
          onChange={(next) => edit({ address: next })}
          required={anyAddress}
          labels={{
            line1: t('settings.company.addressLine1'),
            line2: t('settings.company.addressLine2'),
            city: t('settings.company.city'),
            state: t('settings.company.state'),
            postalCode: t('settings.company.postalCode'),
          }}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm" htmlFor={`${id}-phone`}>
            <span className="mb-1 block font-medium">{t('settings.company.phone')}</span>
            <input
              id={`${id}-phone`}
              type="tel"
              className={FIELD_CLASS}
              value={phone}
              maxLength={30}
              autoComplete="tel"
              onChange={(e) => edit({ phone: e.target.value })}
            />
          </label>
          <label className="block text-sm" htmlFor={`${id}-license`}>
            <span className="mb-1 block font-medium">{t('settings.company.licenseNumber')}</span>
            <input
              id={`${id}-license`}
              className={FIELD_CLASS}
              value={license}
              maxLength={50}
              onChange={(e) => edit({ license: e.target.value })}
            />
          </label>
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={save.isPending || name.trim() === ''}>
            {t('settings.company.saveProfile')}
          </Button>
          {form.dirty ? (
            <Button type="button" size="sm" variant="outline" onClick={discard}>
              {t('settings.company.discard')}
            </Button>
          ) : null}
        </div>
        <Outcome
          saved={saved}
          error={save.isError ? errorText(save.error, t('settings.company.saveFailed')) : null}
        />
      </form>
    </Section>
  );
}

// --- Logo ------------------------------------------------------------------------

function LogoSection({ account }: { account: Account }) {
  const { t } = useTranslation();
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const upload = useUploadLogo();
  const remove = useRemoveLogo();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const uploadButton = useRef<HTMLButtonElement>(null);
  // After a removal the Remove button is gone, and the dialog hands focus back
  // to it; this moves focus to the Upload button instead of the page's body.
  // An effect, so it runs after the dialog's own unmount has returned focus.
  const [refocusUpload, setRefocusUpload] = useState(false);
  useEffect(() => {
    if (!refocusUpload) return;
    uploadButton.current?.focus();
    setRefocusUpload(false);
  }, [refocusUpload]);
  const src = logoSrc(account.logoPath);
  const busy = upload.phase === 'uploading';

  async function onPick(file: File | undefined) {
    if (!file) return;
    setAnnouncement(t('settings.logo.uploading'));
    const ok = await upload.upload(file);
    setAnnouncement(ok ? t('settings.logo.uploaded') : '');
    if (input.current) input.current.value = '';
  }

  const failure =
    upload.phase === 'failed'
      ? upload.failure === 'refused' && upload.message
        ? upload.message
        : t(`settings.logo.failure.${upload.failure ?? 'unknown'}`)
      : null;

  return (
    <Section id={`${id}-heading`} title={t('settings.logo.heading')} description={t('settings.logo.description')}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div
          className="flex h-20 w-48 items-center justify-center rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-muted)]"
          data-testid="logo-preview"
        >
          {src ? (
            <CompanyLogo
              path={account.logoPath}
              alt={t('settings.logo.previewAlt')}
              className="max-h-16 max-w-[11rem] object-contain"
              fallback={
                <span className="text-xs text-[var(--color-muted-foreground)]" data-testid="logo-unavailable">
                  {t('settings.logo.unavailable')}
                </span>
              }
            />
          ) : (
            <span className="text-xs text-[var(--color-muted-foreground)]">{t('settings.logo.none')}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {/* The visible Button is the control; the input is hidden from the
              accessibility tree AND the tab order (web/CONVENTIONS.md, M1-44). */}
          <input
            ref={input}
            type="file"
            accept={LOGO_TYPES.join(',')}
            className="sr-only"
            aria-hidden="true"
            tabIndex={-1}
            data-testid="logo-input"
            onChange={(e) => void onPick(e.target.files?.[0])}
          />
          <Button ref={uploadButton} type="button" size="sm" disabled={busy} onClick={() => input.current?.click()}>
            {src ? t('settings.logo.replace') : t('settings.logo.upload')}
          </Button>
          {src ? (
            <Button type="button" size="sm" variant="outline" disabled={busy || remove.isPending} onClick={() => setConfirmRemove(true)}>
              {t('settings.logo.remove')}
            </Button>
          ) : null}
        </div>
      </div>
      {busy && src ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('settings.logo.keepsCurrent')}</p>
      ) : null}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
      {failure ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]" data-testid="logo-failure">
          {failure}
        </p>
      ) : null}
      {confirmRemove ? (
        <ConfirmDialog
          titleId={`${id}-remove-title`}
          title={t('settings.logo.removeTitle')}
          description={t('settings.logo.removeDescription')}
          confirmLabel={t('settings.logo.remove')}
          isPending={remove.isPending}
          errorMessage={remove.isError ? t('settings.logo.removeFailed') : undefined}
          onConfirm={() =>
            remove.mutate(undefined, {
              onSuccess: () => {
                setConfirmRemove(false);
                setAnnouncement(t('settings.logo.removed'));
                setRefocusUpload(true);
              },
            })
          }
          onClose={() => {
            setConfirmRemove(false);
            remove.reset();
          }}
        />
      ) : null}
    </Section>
  );
}

// --- Hours and timezone ----------------------------------------------------------

const WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
type DayName = (typeof WEEK)[number];
type DayState = { open: boolean; opens: string; closes: string };

/** US zones first — every launch shop is in one — then the rest, by name. */
const COMMON_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

function allZones(current: string): string[] {
  const supported =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  const rest = supported.filter((z) => !COMMON_ZONES.includes(z));
  // The stored zone is always selectable, even one this browser does not list.
  if (!COMMON_ZONES.includes(current) && !rest.includes(current)) rest.push(current);
  return rest.sort();
}

type HoursValues = { timezone: string; days: Record<DayName, DayState> };

function storedHours(account: Account): HoursValues {
  const stored = new Map(account.businessHours.days.map((d) => [d.day, d]));
  return {
    timezone: account.timezone,
    days: Object.fromEntries(
      WEEK.map((day) => {
        const hours = stored.get(day);
        return [day, hours ? { open: true, opens: hours.open, closes: hours.close } : { open: false, opens: '08:00', closes: '17:00' }];
      }),
    ) as Record<DayName, DayState>,
  };
}

function HoursSection({ account }: { account: Account }) {
  const { t } = useTranslation();
  const id = useId();
  const save = useUpdateBusinessHours();
  const form = useStoredForm(storedHours(account));
  const { timezone, days } = form.values;
  const [saved, setSaved] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const zoneSelect = useRef<HTMLSelectElement>(null);
  const zones = useMemo(() => allZones(account.timezone), [account.timezone]);
  const zoneChanges = timezone !== account.timezone;

  // Close after open, on the same day — the API's rule, said here before a
  // round trip. The API still decides.
  const invalid = WEEK.filter((d) => days[d].open && !(days[d].closes > days[d].opens));

  function send(confirmTimezoneChange: boolean) {
    setSaved(null);
    save.mutate(
      {
        timezone,
        confirmTimezoneChange,
        days: WEEK.filter((d) => days[d].open).map((d) => ({ day: d, open: days[d].opens, close: days[d].closes })),
      },
      {
        onSuccess: () => {
          setConfirming(false);
          setSaved(t('settings.hours.saved'));
        },
      },
    );
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (invalid.length > 0) return;
    // ⚠️ A changed zone is never sent without the person agreeing to what it
    // does; the API refuses it (409) without the flag anyway.
    if (zoneChanges) setConfirming(true);
    else send(false);
  }

  function update(day: DayName, change: Partial<DayState>) {
    form.setValues((prev) => ({ ...prev, days: { ...prev.days, [day]: { ...prev.days[day], ...change } } }));
    setSaved(null);
  }

  function setTimezone(next: string) {
    form.setValues((prev) => ({ ...prev, timezone: next }));
    setSaved(null);
  }

  function discard() {
    form.discard();
    setSaved(null);
    save.reset();
    zoneSelect.current?.focus();
  }

  return (
    <Section id={`${id}-heading`} title={t('settings.hours.heading')} description={t('settings.hours.description')}>
      <form className="space-y-4" onSubmit={submit} aria-labelledby={`${id}-heading`}>
        <div className="space-y-1">
          <label className="block text-sm font-medium" htmlFor={`${id}-zone`}>
            {t('settings.hours.timezone')}
          </label>
          <select
            id={`${id}-zone`}
            ref={zoneSelect}
            className={`${FIELD_CLASS} sm:max-w-sm`}
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            <optgroup label={t('settings.hours.commonZones')}>
              {COMMON_ZONES.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </optgroup>
            <optgroup label={t('settings.hours.allZones')}>
              {zones.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </optgroup>
          </select>
          {zoneChanges ? (
            <p className="text-sm text-[var(--color-muted-foreground)]" data-testid="zone-change-note">
              {t('settings.hours.zoneChangeNote')}
            </p>
          ) : null}
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">{t('settings.hours.week')}</legend>
          <ul className="space-y-2">
            {WEEK.map((day) => {
              const state = days[day];
              const label = t(`settings.hours.days.${day}`);
              const bad = invalid.includes(day);
              return (
                <li key={day} className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid={`hours-${day}`}>
                  <label className="flex w-36 items-center gap-2 text-sm" htmlFor={`${id}-${day}-open`}>
                    <input
                      id={`${id}-${day}-open`}
                      type="checkbox"
                      checked={state.open}
                      onChange={(e) => update(day, { open: e.target.checked })}
                    />
                    <span>{label}</span>
                  </label>
                  {state.open ? (
                    <>
                      <label className="sr-only" htmlFor={`${id}-${day}-opens`}>
                        {t('settings.hours.opensOn', { day: label })}
                      </label>
                      <input
                        id={`${id}-${day}-opens`}
                        type="time"
                        step={60}
                        required
                        className={`${FIELD_CLASS} w-32`}
                        value={state.opens}
                        aria-invalid={bad || undefined}
                        aria-describedby={bad ? `${id}-${day}-error` : undefined}
                        onChange={(e) => update(day, { opens: e.target.value })}
                      />
                      <span aria-hidden="true" className="text-sm">
                        –
                      </span>
                      <label className="sr-only" htmlFor={`${id}-${day}-closes`}>
                        {t('settings.hours.closesOn', { day: label })}
                      </label>
                      <input
                        id={`${id}-${day}-closes`}
                        type="time"
                        step={60}
                        required
                        className={`${FIELD_CLASS} w-32`}
                        value={state.closes}
                        aria-invalid={bad || undefined}
                        aria-describedby={bad ? `${id}-${day}-error` : undefined}
                        onChange={(e) => update(day, { closes: e.target.value })}
                      />
                      {bad ? (
                        <span id={`${id}-${day}-error`} className="text-sm text-[var(--color-destructive)]">
                          {t('settings.hours.closeAfterOpen')}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-sm text-[var(--color-muted-foreground)]">{t('settings.hours.closed')}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </fieldset>

        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={save.isPending || invalid.length > 0}>
            {t('settings.hours.save')}
          </Button>
          {form.dirty ? (
            <Button type="button" size="sm" variant="outline" onClick={discard}>
              {t('settings.company.discard')}
            </Button>
          ) : null}
        </div>
        <Outcome
          saved={saved}
          error={save.isError && !confirming ? errorText(save.error, t('settings.company.saveFailed')) : null}
        />
      </form>

      {confirming ? (
        <ConfirmDialog
          titleId={`${id}-zone-title`}
          testId="timezone-confirm"
          title={t('settings.hours.confirmTitle', { from: account.timezone, to: timezone })}
          description={t('settings.hours.confirmDescription')}
          confirmLabel={t('settings.hours.confirmLabel')}
          destructive={false}
          isPending={save.isPending}
          errorMessage={save.isError ? errorText(save.error, t('settings.company.saveFailed')) : undefined}
          onConfirm={() => send(true)}
          onClose={() => {
            setConfirming(false);
            save.reset();
          }}
        />
      ) : null}
    </Section>
  );
}

// --- Payment reminders (M2-16) ---------------------------------------------------

/** The four steps the API offers (`InvoiceReminders.Catalog`), in days from the due date. */
const REMINDER_STEPS = [-3, 7, 14, 30] as const;

/**
 * Which payment reminders the account sends (M2-16, Kyle 2026-10-16): one
 * box per step the API offers, the whole set saved together. Nothing ticked
 * is a real answer — no reminders at all — and says so. The steps are sent
 * in the catalog's order; the API stores them sorted either way, and judges
 * every value (a refusal is shown in its own words).
 */
function RemindersSection({ account }: { account: Account }) {
  const { t } = useTranslation();
  const id = useId();
  const save = useUpdateInvoiceReminders();
  const stored = REMINDER_STEPS.filter((step) => account.invoiceReminderOffsets.map(Number).includes(step));
  const form = useStoredForm<number[]>(stored);
  const [saved, setSaved] = useState<string | null>(null);
  const firstBox = useRef<HTMLInputElement>(null);

  function toggle(step: number, on: boolean) {
    form.setValues((prev) => REMINDER_STEPS.filter((s) => (s === step ? on : prev.includes(s))));
    setSaved(null);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(null);
    save.mutate([...form.values], { onSuccess: () => setSaved(t('settings.reminders.saved')) });
  }

  function discard() {
    form.discard();
    setSaved(null);
    save.reset();
    firstBox.current?.focus();
  }

  return (
    <Section id={`${id}-heading`} title={t('settings.reminders.heading')} description={t('settings.reminders.description')}>
      <form className="space-y-3" onSubmit={submit} aria-labelledby={`${id}-heading`}>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">{t('settings.reminders.legend')}</legend>
          {REMINDER_STEPS.map((step, index) => (
            <label key={step} className="flex items-center gap-2 text-sm" htmlFor={`${id}-step-${step}`}>
              <input
                id={`${id}-step-${step}`}
                ref={index === 0 ? firstBox : undefined}
                type="checkbox"
                checked={form.values.includes(step)}
                onChange={(e) => toggle(step, e.target.checked)}
              />
              <span>{t(`settings.reminders.step.${step}`)}</span>
            </label>
          ))}
        </fieldset>
        {form.values.length === 0 ? (
          <p className="text-sm text-[var(--color-muted-foreground)]" data-testid="reminders-none">
            {t('settings.reminders.noneTicked')}
          </p>
        ) : null}
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={save.isPending}>
            {t('settings.reminders.save')}
          </Button>
          {form.dirty ? (
            <Button type="button" size="sm" variant="outline" onClick={discard}>
              {t('settings.company.discard')}
            </Button>
          ) : null}
        </div>
        <Outcome saved={saved} error={save.isError ? errorText(save.error, t('settings.company.saveFailed')) : null} />
      </form>
    </Section>
  );
}

// --- Appointment emails (M2-21) -------------------------------------------------

/** The arrival windows the API offers (`AppointmentEmails.ArrivalWindowChoices`), in minutes. */
const ARRIVAL_WINDOWS = [0, 60, 120, 180, 240] as const;

/** Every whole hour, `"HH:00"` — the only reminder times the API takes. */
const REMINDER_HOURS = Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, '0')}:00`);

/**
 * "9:00 AM" for `"09:00"`: a time of day with no date and no zone, so formatted
 * on UTC. Minutes are read too — the off-list stored value this list keeps is
 * the one case that is not a whole hour, and labelling "09:30" as "9:00 AM"
 * would offer two options that read the same. A value that is not `HH:MM` at
 * all is shown as stored rather than throwing on an invalid date.
 */
function hourLabel(value: string): string {
  const match = /^(\d{1,2}):(\d{2})/.exec(value);
  const hour = match ? Number(match[1]) : NaN;
  const minute = match ? Number(match[2]) : NaN;
  if (!(hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59)) return value;
  return formatDate(new Date(Date.UTC(2000, 0, 1, hour, minute)), { timeStyle: 'short', timeZone: 'UTC' });
}

type AppointmentValues = {
  confirmationsEnabled: boolean;
  remindersEnabled: boolean;
  reminderTime: string;
  arrivalWindowMinutes: number;
};

function storedAppointments(account: Account): AppointmentValues {
  return {
    confirmationsEnabled: account.appointmentConfirmationsEnabled,
    remindersEnabled: account.appointmentRemindersEnabled,
    reminderTime: account.appointmentReminderTime,
    arrivalWindowMinutes: Number(account.appointmentArrivalWindowMinutes),
  };
}

/**
 * The account's appointment emails (M2-21, Kyle's 1, 5 and 7): confirmations
 * (and the "updated" and "canceled" emails that follow them) and day-before
 * reminders, each a switch; the hour reminders go, on each PROPERTY's clock;
 * and how wide the arrival window the emails promise is. Saved together, as
 * the API replaces them together; a refusal is shown in the API's words.
 *
 * A stored hour that is not in the list (the API only takes whole hours, so
 * this is belt and braces) is still offered, so opening the form never
 * silently changes it.
 */
function AppointmentEmailsSection({ account }: { account: Account }) {
  const { t } = useTranslation();
  const id = useId();
  const save = useUpdateAppointmentEmailSettings();
  const form = useStoredForm(storedAppointments(account));
  const { confirmationsEnabled, remindersEnabled, reminderTime, arrivalWindowMinutes } = form.values;
  const [saved, setSaved] = useState<string | null>(null);
  const firstBox = useRef<HTMLInputElement>(null);
  const hours = REMINDER_HOURS.includes(reminderTime) ? REMINDER_HOURS : [...REMINDER_HOURS, reminderTime];

  function edit(change: Partial<AppointmentValues>) {
    form.setValues((prev) => ({ ...prev, ...change }));
    setSaved(null);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(null);
    save.mutate({ ...form.values }, { onSuccess: () => setSaved(t('settings.appointments.saved')) });
  }

  function discard() {
    form.discard();
    setSaved(null);
    save.reset();
    firstBox.current?.focus();
  }

  return (
    <Section
      id={`${id}-heading`}
      title={t('settings.appointments.heading')}
      description={t('settings.appointments.description')}
    >
      <form className="space-y-3" onSubmit={submit} aria-labelledby={`${id}-heading`}>
        <div className="space-y-2">
          <div className="text-sm">
            <label className="flex items-center gap-2 font-medium" htmlFor={`${id}-confirmations`}>
              <input
                id={`${id}-confirmations`}
                ref={firstBox}
                type="checkbox"
                checked={confirmationsEnabled}
                aria-describedby={`${id}-confirmations-hint`}
                onChange={(e) => edit({ confirmationsEnabled: e.target.checked })}
              />
              {t('settings.appointments.confirmations')}
            </label>
            <p id={`${id}-confirmations-hint`} className="ml-6 text-[var(--color-muted-foreground)]">
              {t('settings.appointments.confirmationsHint')}
            </p>
          </div>
          <div className="text-sm">
            <label className="flex items-center gap-2 font-medium" htmlFor={`${id}-reminders`}>
              <input
                id={`${id}-reminders`}
                type="checkbox"
                checked={remindersEnabled}
                aria-describedby={`${id}-reminders-hint`}
                onChange={(e) => edit({ remindersEnabled: e.target.checked })}
              />
              {t('settings.appointments.reminders')}
            </label>
            <p id={`${id}-reminders-hint`} className="ml-6 text-[var(--color-muted-foreground)]">
              {t('settings.appointments.remindersHint')}
            </p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="block text-sm font-medium" htmlFor={`${id}-time`}>
              {t('settings.appointments.reminderTime')}
            </label>
            <select
              id={`${id}-time`}
              className={FIELD_CLASS}
              value={reminderTime}
              aria-describedby={`${id}-time-hint`}
              onChange={(e) => edit({ reminderTime: e.target.value })}
            >
              {hours.map((hour) => (
                <option key={hour} value={hour}>
                  {hourLabel(hour)}
                </option>
              ))}
            </select>
            <p id={`${id}-time-hint`} className="text-sm text-[var(--color-muted-foreground)]">
              {t('settings.appointments.reminderTimeHint')}
            </p>
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium" htmlFor={`${id}-window`}>
              {t('settings.appointments.window')}
            </label>
            <select
              id={`${id}-window`}
              className={FIELD_CLASS}
              value={arrivalWindowMinutes}
              aria-describedby={`${id}-window-hint`}
              onChange={(e) => edit({ arrivalWindowMinutes: Number(e.target.value) })}
            >
              {ARRIVAL_WINDOWS.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {minutes === 0
                    ? t('settings.appointments.windowNone')
                    : t('settings.appointments.windowHours', { count: minutes / 60 })}
                </option>
              ))}
            </select>
            <p id={`${id}-window-hint`} className="text-sm text-[var(--color-muted-foreground)]">
              {t('settings.appointments.windowHint')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={save.isPending}>
            {t('settings.appointments.save')}
          </Button>
          {form.dirty ? (
            <Button type="button" size="sm" variant="outline" onClick={discard}>
              {t('settings.company.discard')}
            </Button>
          ) : null}
        </div>
        <Outcome saved={saved} error={save.isError ? errorText(save.error, t('settings.company.saveFailed')) : null} />
      </form>
    </Section>
  );
}

// --- Recent changes --------------------------------------------------------------

function RecentChangesSection() {
  const { t } = useTranslation();
  const id = useId();
  const changes = useSettingsChanges();
  const timeZone = useAccount().data?.timezone;

  return (
    <Section id={`${id}-heading`} title={t('settings.changes.heading')} description={t('settings.changes.description')}>
      {changes.isPending ? (
        <LoadingState label={t('settings.changes.loading')} />
      ) : changes.isError ? (
        <ErrorState title={t('settings.changes.loadErrorTitle')} onRetry={() => void changes.refetch()} />
      ) : changes.data.length === 0 ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('settings.changes.none')}</p>
      ) : (
        <ul className="divide-y divide-[var(--color-border)] text-sm" data-testid="settings-changes">
          {changes.data.map((change, index) => (
            <li key={`${change.changedAt}-${index}`} className="py-2" data-testid="settings-change">
              <p>
                <span className="font-medium">{t(`settings.changes.area.${change.area}`, { defaultValue: change.area })}</span>
                {': '}
                <span className="break-words">{change.detail}</span>
              </p>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                {change.changedBy
                  ? t('settings.changes.byOn', {
                      who: change.changedBy,
                      when: formatInstantWithZone(change.changedAt, timeZone),
                    })
                  : formatInstantWithZone(change.changedAt, timeZone)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
